// v0.90.21 — the rest of a first payment, held on THIS device.
//
// A first payment to someone new sends €1 (a bearer Drop) and holds the rest
// here until the recipient says the €1 arrived. It names a person and carries
// the Drop's claim key, so by the settings-sync rule it never leaves the
// device: no server copy, no sync. Nothing here moves money — it is a note of
// what the sender meant to send, acted on only by the sender's own slide.

export interface Held {
  id: string;         // the €1 Drop's id (the rest card is /c/{id})
  label: string;      // the sender's name for the recipient ("Lucy")
  currency: string;   // "USD" | "EUR"
  first: string;      // "1.00" — the bearer Drop
  rest: string;       // "49.00" — held back
  ref?: string;
  secret: string;     // the claim key's private half (to re-share the link)
  createdAt: number;
}

const KEY = "m.held";

/**
 * v1.0.0 — the label of a Drop made with "Someone new" (no name typed). The
 * Worker needs a label, so this is it; everywhere a person would be named,
 * the recipient's own profile name is used once they've claimed, and this
 * label is never saved as a contact's name.
 */
export const NEW_LABEL = "Someone new";
export const isNewLabel = (l: string | null | undefined) => l === NEW_LABEL;

function readAll(): Held[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(v) ? (v as Held[]) : [];
  } catch {
    return [];
  }
}
function writeAll(all: Held[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* storage blocked — the rest can still be sent by hand */
  }
}

export const listHeld = (): Held[] => readAll();
export const getHeld = (id: string): Held | null => readAll().find((h) => h.id === id) ?? null;
export function addHeld(h: Held) {
  writeAll([h, ...readAll().filter((x) => x.id !== h.id)]);
}
// An unsplit Drop (1 or less) has no rest, but its link still needs its key to
// be re-shared, and its label is what the recipient is saved as. Same rule:
// this device only.
const SECRETS = "m.dropKeys";
type Kept = { secret: string; label: string };
function readKept(): Record<string, Kept> {
  try {
    return JSON.parse(localStorage.getItem(SECRETS) ?? "{}") as Record<string, Kept>;
  } catch {
    return {};
  }
}
export function keepSecret(id: string, secret: string, label: string) {
  try {
    const all = readKept();
    all[id] = { secret, label };
    localStorage.setItem(SECRETS, JSON.stringify(all));
  } catch {
    /* storage blocked */
  }
}
export function secretFor(id: string): string | null {
  return getHeld(id)?.secret ?? readKept()[id]?.secret ?? null;
}
/** Your name for the recipient of Drop `id`, if this device made it. */
export function labelFor(id: string): string | null {
  return getHeld(id)?.label ?? readKept()[id]?.label ?? null;
}
export function removeHeld(id: string) {
  writeAll(readAll().filter((x) => x.id !== id));
}
/** Held rests whose label matches what's typed in To (prefix/contains, any case). */
export function heldMatching(q: string): Held[] {
  const ql = q.trim().toLowerCase();
  if (!ql) return [];
  return readAll().filter((h) => h.label.toLowerCase().includes(ql));
}
